// imports
import _ from "lodash";
import { Bike } from "./Bike";

export class Change {
  constructor(operation, part) {
    this.operation = operation;
    this.part = part;
  }
}
Change.OPERATIONS = {
  ADD: "add",
  REMOVE: "remove",
};
Change.add = (part) => {
  return new Change(Change.OPERATIONS.ADD, part);
};
Change.remove = (part) => {
  return new Change(Change.OPERATIONS.REMOVE, part);
};

/**
 * A Mechanic can add and remove parts from a bike.
 * Calling build will return a change set and a new bike or an object containing the validation errors
 */
export class Mechanic {
  // ========================================
  // Constructor
  // ========================================

  constructor(bike = new Bike(), inventory) {
    // instance variables
    this._bike = bike;
    this._changes = [];
    this._inventory = inventory;
  }

  removePart(part) {
    let model = this._bike.model;
    if (model.id === part.id) {
      console.debug(`tried to remove model ${part.id}, this is not allowed`);
      return;
    }
    let removingStock = Object.entries(model.restrictions).some(
      ([subcategory, partIds]) => {
        return partIds.some((partId) => partId == part.id);
      }
    );
    if (removingStock) {
      console.debug(
        `tried to remove stock item ${part.id}, this is not allowed`
      );
      return;
    }
    this._changes.push(Change.remove(part));
  }

  addPart(part) {
    this._changes.push(Change.add(part));
  }

  build() {
    const { validBikeParts, changeIterations, changes } = validateBuild(
      this._bike,
      this._changes,
      this._inventory
    );

    return {
      changes,
      bike: new Bike({ parts: validBikeParts }),
      validationFixes: changeIterations,
    };
  }
}

/* example restrictions
"restrictions": {
  "1.BRP_SWI_SIDE_PANELS_ACCY": [
    "1.s.BRP_SWI_SIDE_PANELS_ACCY.219400803",
    "1.s.BRP_SWI_SIDE_PANELS_ACCY.219400804",
    "1.s.BRP_SWI_SIDE_PANELS_ACCY.219400805",
    "1.s.BRP_SWI_SIDE_PANELS_ACCY.219400809",
    "1.s.BRP_SWI_SIDE_PANELS_ACCY.219400923",
    "1.s.BRP_SWI_SIDE_PANELS_ACCY.219400928",
    null
  ],
  "1.BRP_SWI_HOOD_TRIM_ACCY": [
    "1.s.BRP_SWI_HOOD_TRIM_ACCY.219400873",
    "1.s.BRP_SWI_HOOD_TRIM_ACCY.219400874",
    "1.s.BRP_SWI_HOOD_TRIM_ACCY.219400875",
    null
  ]
},
*/

function validateBuild(bike, changes, inventory) {
  const startingParts = bike.parts;
  const iterations = [{ parts: startingParts, changes }];
  const pendingChanges = [...changes];
  let pendingChange;
  while ((pendingChange = pendingChanges.shift())) {
    // validate change
    const iteration = validateChange(
      iterations[iterations.length - 1].parts,
      pendingChange,
      inventory
    );
    changes.unshift(...iteration.pendingChanges);
    pendingChanges.unshift(...iteration.pendingChanges);
    iterations.push(iteration);
  }
  // validate stock parts (this could be heavy but since this is part of a human operation and not m2m we should be fine)
  let changedParts = iterations[iterations.length - 1].parts;
  const stockChanges = missingStockParts(changedParts, inventory);
  if (stockChanges.missingChanges.length > 0) {
    // adding stock parts are silent
    let { missingParts, missingChanges } = stockChanges;
    changedParts.push(...missingParts);
    changes.push(...missingChanges);
  }
  return {
    validBikeParts: changedParts, // use the last iteration as the bike
    changeIterations: iterations,
    changes,
  };
}

function validateChange(startingParts, change, inventory) {
  // assign the parts to their subcategories
  const partsMap = startingParts.reduce((map, part) => {
    map[part.id] = part;
    return map;
  }, {});
  const subcategories = startingParts.reduce((acc, part) => {
    acc[part.subcategoryId] = part;
    return acc;
  }, {});

  const pendingChanges = [];
  if (change.operation === Change.OPERATIONS.ADD) {
    // is there already a different part in this subcategory
    if (
      subcategories[change.part.subcategoryId] &&
      subcategories[change.part.subcategoryId].id !== change.part.id
    ) {
      // add a remove operation for the current part in the subcategory
      Log(
        `[Mechanic] removing current part ${
          subcategories[change.part.subcategoryId].id
        }`
      );
      pendingChanges.push(
        Change.remove(subcategories[change.part.subcategoryId])
      );
      delete partsMap[subcategories[change.part.subcategoryId].id];
      delete subcategories[change.part.subcategoryId];
    }

    // add the part to the parts map and subcategory map
    partsMap[change.part.id] = change.part;
    subcategories[change.part.subcategoryId] = change.part;

    // find any parts that have restrictions on the subcategory of the new part
    const oldPartsNeedingNewPartSubcategory = Object.values(partsMap).filter(
      (part) => {
        // part has no restrictions
        if (!part.restrictions) {
          return false;
        }
        const restrictionsForSubcategory = Object.entries(
          part.restrictions
        ).filter(([subcategoryId, partIds]) => {
          return subcategoryId === change.part.subcategoryId;
        });
        return restrictionsForSubcategory.length > 0;
      }
    );
    oldPartsNeedingNewPartSubcategory.forEach((oldPart) => {
      // does the found part not allow this new part
      const oldPartCompatible = oldPart.restrictions[
        change.part.subcategoryId
      ].some((restrictedIds) => restrictedIds === change.part.id);
      if (oldPartCompatible === false) {
        // add a remove operation for the found part to the pending changes
        Log(
          `[Mechanic] removing conflicting part ${subcategories[oldPart.id]}`
        );
        pendingChanges.push(Change.remove(oldPart));
      }
    });

    // does this part require any new parts to be added
    Object.entries(change.part.restrictions || {}).forEach(
      ([subcategoryId, partIds]) => {
        // do we not have any of the required parts for this subcategory
        const currentPart = _.get(subcategories, [subcategoryId], null);
        const currentPartId = currentPart ? currentPart.id : null;
        const currentPartCompatible = partIds.some(
          (partId) => partId === currentPartId
        );
        if (currentPartCompatible === false) {
          if (partIds.some((partId) => partId === null)) {
            // if the part allows null remove the current part
            Log(
              `[Mechanic] removing conflicting part ${currentPartId} and replacing it with none`
            );
            pendingChanges.push(Change.remove(currentPart));
          } else {
            // add an add operation for the subcategory
            const partToAdd = inventory.find((part) => part.id === partIds[0]);
            Log(
              `[Mechanic] removing conflicting part ${currentPartId} and replacing it with ${partToAdd.id}`
            );
            pendingChanges.push(Change.add(partToAdd));
          }
        }
      }
    );

    // does this part have any initial parts
    if (change.part.initialParts) {
      change.part.initialParts.forEach((partId) => {
        let partToAdd = inventory.find((part) => part.id === partId);
        Log(`[Mechanic] adding initial part ${partToAdd.id}`);
        pendingChanges.push(Change.add(partToAdd));
      });
    }
  } else if (change.operation == Change.OPERATIONS.REMOVE) {
    // is the part still in the list
    if (partsMap[change.part.id]) {
      // remove the part
      delete partsMap[change.part.id];
      delete subcategories[change.part.subcategoryId];
    }
    // find any parts that require the subcategory of the removed part
    const oldPartsNeedingRemovedPartSubcategory = Object.values(
      partsMap
    ).filter((part) => {
      // part has no restrictions
      if (!part.restrictions) {
        return false;
      }
      const restrictionsForSubcategory = Object.entries(
        part.restrictions
      ).filter(([subcategoryId, partIds]) => {
        return subcategoryId === change.part.subcategoryId;
      });
      return restrictionsForSubcategory.length > 0;
    });
    oldPartsNeedingRemovedPartSubcategory.forEach((part) => {
      // does the found part allow the new part
      Object.entries(part.restrictions).forEach(([subcategoryId, partIds]) => {
        // this isn't the restriction for our part
        if (subcategoryId !== change.part.subcategoryId) {
          return;
        }
        const currentPart = _.get(subcategories, [subcategoryId], null);
        const currentPartId = currentPart ? currentPart.id : null;
        const currentPartCompatible = partIds.some(
          (partId) => partId === currentPartId
        );
        if (currentPartCompatible == false) {
          // add a remove operation of the found part to the pending changes
          Log(
            `[Mechanic] removing part ${part.id} the relied on a part we are removing ${change.part.id}`
          );
          pendingChanges.push(Change.remove(part));
        }
      });
    });
  }

  return {
    change,
    parts: Object.values(partsMap),
    pendingChanges,
  };
}

function missingStockParts(parts, inventory) {
  // find the model
  const model = parts.find(
    (part) => part.sku === "AR" || part.sku === "STD" || part.sku === "SPORT"
  );
  if (model == undefined) {
    return console.error("this bike doesn't have a model!");
  }

  const missingParts = [];
  const missingChanges = [];
  const partsMap = inventory.reduce((map, part) => {
    map[part.id] = part;
    return map;
  }, {});
  const subcategoryMap = parts.reduce((acc, part) => {
    acc[part.subcategoryId] = part;
    return acc;
  }, {});
  // loop through the stock items
  model.stock.forEach((stockDefinition) => {
    // do the subcategories have parts?
    let hasPart = false;
    stockDefinition.subcategories.forEach((subcategory) => {
      if (subcategoryMap[subcategory] != undefined) {
        hasPart = true;
      }
    });
    if (hasPart === false) {
      const missingPart = partsMap[stockDefinition.part];
      Log(`[Mechanic] adding missing stock part ${missingPart.id}`);
      missingChanges.push(new Change.add(missingPart));
      missingParts.push(missingPart);
    }
  });
  return {
    missingParts,
    missingChanges,
  };
}

function Log(...args) {
  if (false) {
    console.log(...args);
  }
}
// const pendingChanges = [change];
// if (change.operation == "add") {
//   // is there already a different part in this subcategory
//     // add a remove operation for the current part in the subcategory
//   // find any parts that require the subcategory of the new part
//     // does the found part not allow this new part
//       // add a remove operation for the found part to the pending changes
//   // does this part require any new parts to be added
//     // do we not have any of the required parts for this subcategory
//       // add an add operation for the subcategory
// } else if (change.operation == "remove") {
//   // is the part still in the list
//     // remove the part
//   // find any parts that require the subcategory of the removed part
//     // does the found part allow the new part
//       // add a remove operation of the found part to the pending changes
// }



// WEBPACK FOOTER //
// ./src/managers/Garage/Mechanic.js